// Кабинет администратора (/admin) — отдельное приложение в оформлении «Непростобота»: тёмное меню слева,
// разделы «Пользователи», «Аналитика», «База законов», «Логи». Раздел — в адресе (?tab=…), «Назад» в браузере работает.
// Не администратору — экран входа (логин и пароль или аккаунт администратора).
import { useEffect, useState } from "react";
import { postJson } from "../api";
import { boot, MARK_URL } from "../boot";
import { LoginDialog } from "../components/Dialogs";
import { Icon } from "../components/Icon";
import { logout } from "../lib/account";
import { openLogin } from "../store";
import { AnalyticsSection, LawsSection, LogsSection } from "./InsightsSections";
import { userTitle, type AdminUser } from "./shared";
import { UsersSection } from "./UsersSection";

type Tab = "users" | "analytics" | "laws" | "logs";
const TABS: [Tab, string, string][] = [
  ["users", "Пользователи", "users"], ["analytics", "Аналитика", "chart"], ["laws", "База законов", "scales"], ["logs", "Логи", "lines"],
];

const tabOf = (): Tab => {
  const tab = new URLSearchParams(location.search).get("tab");
  return TABS.some(([value]) => value === tab) ? (tab as Tab) : "users";
};

function Brand() {
  return (
    <a className="adm-brand" href="/admin">
      <span className="adm-brand-mark"><img src={MARK_URL} alt="" width="18" height="18" /></span>
      <span>Правосвод</span>
    </a>
  );
}

function Gate() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const user = boot.user;
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await postJson("/api/admin/login", { login, password });
      location.reload();
    } catch (failure) {
      setError((failure as Error).message);
      setBusy(false);
    }
  }
  return (
    <div className="adm adm-gate">
      <div className="adm-card adm-gate-card">
        <Brand />
        <h1>Кабинет администратора</h1>
        {boot.adminPassword ? (
          <form onSubmit={submit}>
            <label>Логин<input value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="username" required autoFocus /></label>
            <label>Пароль<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required /></label>
            {error && <p className="adm-error" role="alert">{error}</p>}
            <button type="submit" className="adm-btn is-primary is-block" disabled={busy}>{busy ? "Проверяю…" : "Войти в кабинет"}</button>
          </form>
        ) : <p className="adm-muted">Вход по логину и паролю ещё не настроен.</p>}
        <div className="adm-gate-alt">
          {user ? (
            <>
              <p>Вы вошли на сайт как <strong>{user.email}</strong> — у этого аккаунта нет доступа к кабинету.</p>
              <button type="button" className="adm-btn is-ghost" onClick={logout}><Icon name="logout" />Выйти из аккаунта</button>
            </>
          ) : (
            <>
              <p>Или войдите на сайте аккаунтом администратора.</p>
              <button type="button" className="adm-btn is-ghost" onClick={() => openLogin()}><Icon name="login" />Войти аккаунтом</button>
            </>
          )}
          <a className="adm-link" href="/">← На сайт Правосвода</a>
        </div>
      </div>
      <LoginDialog />
    </div>
  );
}

export function AdminApp() {
  const [tab, setTab] = useState<Tab>(tabOf);
  const [menu, setMenu] = useState(false);                         // меню на телефоне
  const [logsUser, setLogsUser] = useState<{ id: number; title: string } | null>(null);

  useEffect(() => {
    const back = () => setTab(tabOf());
    window.addEventListener("popstate", back);
    return () => window.removeEventListener("popstate", back);
  }, []);
  useEffect(() => {
    document.title = boot.isAdmin ? `${TABS.find(([value]) => value === tab)![1]} — кабинет Правосвода` : "Кабинет администратора — Правосвод";
  }, [tab]);

  if (!boot.isAdmin) return <Gate />;

  function open(next: Tab) {
    setMenu(false);
    if (next !== "logs") setLogsUser(null);
    if (next === tab) return;
    history.pushState(null, "", next === "users" ? "/admin" : `/admin?tab=${next}`);
    setTab(next);
    window.scrollTo(0, 0);
  }
  function showLogs(user: AdminUser) {
    setLogsUser({ id: user.id, title: userTitle(user) });
    history.pushState(null, "", "/admin?tab=logs");
    setTab("logs");
    window.scrollTo(0, 0);
  }

  return (
    <div className={`adm adm-layout ${menu ? "is-menu" : ""}`}>
      <aside className="adm-side" aria-label="Меню кабинета">
        <Brand />
        <p className="adm-side-label">Основные</p>
        <nav>
          {TABS.map(([value, label, icon]) => (
            <button key={value} type="button" className={`adm-nav ${tab === value ? "is-active" : ""}`} aria-current={tab === value ? "page" : undefined}
              onClick={() => open(value)}>
              <Icon name={icon} />{label}
            </button>
          ))}
        </nav>
        <div className="adm-side-foot">
          <a className="adm-nav" href="/"><Icon name="arrow" />На сайт</a>
          {boot.adminSession && (
            <button type="button" className="adm-nav" onClick={() => postJson("/api/admin/logout", {}).finally(() => location.reload())}>
              <Icon name="logout" />Выйти из кабинета
            </button>
          )}
        </div>
      </aside>
      <div className="adm-scrim" onClick={() => setMenu(false)} />
      <main className="adm-main">
        {tab === "users" && <UsersSection onLogs={showLogs} />}
        {tab === "analytics" && <AnalyticsSection />}
        {tab === "laws" && <LawsSection />}
        {tab === "logs" && <LogsSection user={logsUser} onReset={() => setLogsUser(null)} />}
      </main>
      <button type="button" className="adm-menu-toggle" aria-expanded={menu} onClick={() => setMenu(!menu)}>
        {menu ? "Скрыть меню" : "Показать меню"}
      </button>
    </div>
  );
}
